/* Traduction cote app des messages renvoyes par l'API Django.

   Le backend repond en anglais ou en francais fixes (pas de LocaleMiddleware,
   pas de fichiers .po) et rarement avec un code stable. On reconnait donc les
   messages connus (texte exact ou motif) et on affiche la cle i18n
   correspondante. Les champs `code` sont pris en compte en priorite quand le
   serveur les envoie. */
import { countryNameFromSlug } from "./i18nFormat";

const norm = (s) => String(s == null ? "" : s).trim();
// Valeurs inserees affichees comme texte par React (deja echappe) : sans ceci,
// i18next transformerait "&" ou "/" en entites visibles ("Bosnia &amp; ...").
const RAW = { interpolation: { escapeValue: false } };
const firstMsg = (v) => (Array.isArray(v) ? norm(v[0]) : norm(v));

/** Cherche le 1er motif qui correspond ; renvoie la cle i18n ou null. */
const matchKey = (msg, table) => {
  const m = norm(msg);
  if (!m) return null;
  for (const [re, key] of table) {
    if (typeof re === "string" ? m === re : re.test(m)) return key;
  }
  return null;
};

/* ---------- Commandes (annuler / terminer) — orders/views.py ---------- */
const ORDER_MESSAGES = [
  [/^order not found/i, "signal.err-order-not-found"],
  [/^order is already successful/i, "signal.err-order-received"],
  [/^order is already finished/i, "signal.err-order-finished"],
  [/^order is already cancell?ed/i, "signal.err-order-cancelled"],
  [/^order is already expired/i, "signal.err-order-expired"],
  [/^sms still pending/i, "signal.err-sms-pending"],
];

/** Message d'erreur traduit pour l'annulation / la finalisation d'une commande. */
export const orderActionError = (err, t) => {
  const res = err && err.response;
  if (!res) return t("signal.t-try-again");
  const msg = res.data && res.data.message;
  const key = matchKey(msg, ORDER_MESSAGES);
  if (key) return t(key);
  if (res.status === 404) return t("signal.err-order-not-found");
  return t("signal.t-try-again");
};

/* ---------- Depot — orders/views.py CreateDepositView ---------- */
const DEPOSIT_CODES = {
  dep_busy: "signal.dep-busy", deposit_busy: "signal.dep-busy", busy: "signal.dep-busy",
  dep_unavailable: "signal.dep-unavailable", deposit_unavailable: "signal.dep-unavailable",
  dep_provider_down: "signal.dep-provider-down", payment_down: "signal.dep-provider-down",
  dep_bad_currency: "signal.dep-bad-currency", bad_ccy: "signal.dep-bad-currency",
  ccy_missing: "signal.dep-bad-currency",
  ccy_unavailable: "signal.dep-ccy-unavailable", dep_ccy_unavailable: "signal.dep-ccy-unavailable",
};

/** Message d'erreur traduit pour la creation d'un depot.
    `ctx` : { min, coin } pour les messages parametres. */
export const depositError = (err, t, ctx = {}) => {
  const res = err && err.response;
  if (!res) return t("signal.dep-failed");
  const d = res.data || {};
  const msg = norm(d.message);
  const code = norm(d.code).toLowerCase().replace(/-/g, "_");
  const coin = d.currency || ctx.coin || "";

  if (code && DEPOSIT_CODES[code]) return t(DEPOSIT_CODES[code], { coin, ...RAW });
  // ff.io refuse la devise en ce moment ("... currency is not available for receiving")
  if (/not available for receiving/i.test(msg)) return t("signal.dep-ccy-unavailable", { coin, ...RAW });
  if (res.status === 429) return t("signal.dep-busy");
  if (res.status === 503) {
    return /ne r[ée]pond pas/i.test(msg) ? t("signal.dep-provider-down") : t("signal.dep-unavailable");
  }
  if (res.status === 400) {
    // Erreurs du serializer de montant (orders/serializers.py) : {amount: [...]}
    const amountMsg = firstMsg(d.amount);
    if (amountMsg) {
      if (/maximum|no more than/i.test(amountMsg)) return t("signal.dep-max-error", { max: "10,000" });
      return t("signal.dep-min-error", { min: ctx.min || 1 });
    }
    // Devise absente / inconnue, ou refus ff.io (codes 301/302)
    return t("signal.dep-bad-currency");
  }
  return t("signal.dep-failed");
};

/* ---------- Inscription — users/views.py + validateurs Django ---------- */
const SIGNUP_MESSAGES = [
  [/already exists/i, "signal.auth-email-exists"],
  [/permanent email/i, "signal.auth-email-disposable"],
  [/valid email/i, "signal.auth-email-invalid"],
  [/too short/i, "signal.auth-pass-short"],
  [/too common/i, "signal.auth-pass-common"],
  [/entirely numeric/i, "signal.auth-pass-numeric"],
  [/too similar/i, "signal.auth-pass-similar"],
  [/passwords? (and confirm password )?do(es)? not match|didn't match/i, "signal.pw-mismatch"],
  [/complete the captcha/i, "signal.auth-captcha"],
  [/captcha/i, "signal.auth-captcha-failed"],
  [/this field is required|may not be blank/i, "signal.auth-required"],
];

/** Message traduit pour une erreur d'inscription ; repli sur le texte d'origine
    s'il est inconnu (il dit au client quoi corriger). */
export const signupError = (err, t) => {
  const res = err && err.response;
  if (!res) return t("signal.t-try-again");
  const d = res.data || {};
  const raw =
    firstMsg(d.email) || firstMsg(d.password) || firstMsg(d.confirm_password) || norm(d.message);
  const key = matchKey(raw, SIGNUP_MESSAGES);
  if (key) return t(key);
  if (res.status >= 500 || !raw) return t("signal.t-try-again");
  return raw;
};

/* ---------- Historique de solde : motif (champ `reason`) ---------- */
const daysLabel = (n, t) => `${n} ${Number(n) > 1 ? t("signal.days") : t("signal.day")}`;

/** Libelle traduit d'un mouvement de solde. Le serveur ecrit `reason` en
    francais (ff_deposits.py, models.py, rent_service.py, views.py) : on
    reconnait ces motifs. Motif inconnu (ajustement manuel) : texte d'origine. */
export const reasonLabel = (item, t) => {
  const r = norm(item && item.reason);
  let m;
  // Depot ff.io : "Depot ff.io <id> (<montant> <devise>)"
  if ((m = /^D[ée]p[oô]t ff\.io (\S+) \(([\d.,]+) ([A-Za-z0-9]+)\)/i.exec(r))) {
    return t("signal.reason-deposit-amount", { id: m[1], amount: m[2], ccy: m[3], ...RAW });
  }
  if ((m = /^D[ée]p[oô]t (?:ff\.io )?(\S+)/i.exec(r))) {
    return t("signal.reason-deposit", { id: m[1], ...RAW });
  }
  if (/^Achat de num[ée]ro \(API\)/i.test(r)) return t("signal.reason-purchase-api");
  if (/^Achat de num[ée]ro/i.test(r)) return t("signal.reason-purchase");
  if ((m = /^Remboursement commande #(\d+) ([^/]+)\/(.+)$/i.exec(r))) {
    return t("signal.reason-refund-order", {
      id: m[1],
      country: countryNameFromSlug(m[2]),
      service: /^other$/i.test(m[3]) ? t("signal.service-other") : m[3],
      ...RAW,
    });
  }
  if ((m = /^Remboursement commande #(\d+)/i.exec(r))) {
    return t("signal.reason-refund-order-short", { id: m[1] });
  }
  if ((m = /^Remboursement location #(\d+) \(sans SMS\)/i.exec(r))) {
    return t("signal.reason-refund-rent-nosms", { id: m[1] });
  }
  if ((m = /^Remboursement location #(\d+)/i.exec(r))) {
    return t("signal.reason-refund-rent", { id: m[1] });
  }
  // "Location <pays> <n>j (<fournisseur>)" : le fournisseur n'est pas affiche.
  if ((m = /^Location (\S+) (\d+)\s*j/i.exec(r))) {
    return t("signal.reason-rent", {
      country: countryNameFromSlug(m[1]), duration: daysLabel(m[2], t), ...RAW,
    });
  }
  return r || "—";
};
